'use client';

import { useEffect, useState } from 'react';
import { Body, Heading, Modal, TeamSection } from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import { defaultLanguage } from '@stereoagency/constants';
import flush from 'just-flush';
import {
  createDocumentURL,
  findInKeyArray,
  formatProfileName,
  sanityImageAsProps
} from '@/utils';
import { SimpleBlockContent } from '@/components/sanity/client';
import { diseaseModalQueryBuilder } from './diseaseModalQueryBuilder';

const query = diseaseModalQueryBuilder.use();

type Props = {
  disease: (typeof query)[1][number] | null;
  onClose: () => void;
};

export default function DiseaseModal({ disease, onClose }: Props) {
  const { t, lang } = useTranslation();
  const [internalValue, setInternalValue] = useState(disease);

  const name = internalValue?.name
    ? findInKeyArray(internalValue.name, [lang, defaultLanguage.id])
    : null;

  const content = internalValue?.content
    ? findInKeyArray(internalValue.content, [lang, defaultLanguage.id])
    : null;

  const researchGroups = flush(
    disease?.researchGroups.map((group) => {
      if (
        !group.researchGroupLeader?.image?.asset ||
        !group.researchGroupLeader.name
      ) {
        return null;
      }

      return {
        image: sanityImageAsProps(group.researchGroupLeader.image),
        name: formatProfileName(group.researchGroupLeader),
        action: group.slug?.current
          ? {
              href: createDocumentURL(lang, 'researchGroup', group.slug.current)
            }
          : undefined
      };
    }) ?? []
  );

  useEffect(() => {
    if (disease) setInternalValue(disease);
  }, [disease]);

  return (
    <Modal open={!!disease} onClose={onClose}>
      <Heading as="h2" variant="h2" className="mb-5">
        {name?.value}
      </Heading>
      <Body className="prose">
        <SimpleBlockContent value={content?.value ?? []} />
      </Body>
      {!!researchGroups.length && (
        <div className="mt-6 md:mt-9">
          <Heading as="h3" variant="h3" className="mb-5">
            {t('global:principal-investigators')}
          </Heading>
          <TeamSection team={researchGroups} />
        </div>
      )}
    </Modal>
  );
}
